import { ActiveFilter, FilterProvider } from "~/components/ultra-filter/types";
import React, { ReactNode } from "react";
import { FilterProviderProps } from "~/filterProviders/types";
import {
  yesNoOptions,
  YesNoPicker,
} from "~/filterProviders/YesNoPickerFormField";

type CreateYesNoProviderArgs = FilterProviderProps & {
  label: string;
  quickFiltering: string;
  type: string;
  render: () => ReactNode;
};

export const createYesNoProvider = (
  props: CreateYesNoProviderArgs
): FilterProvider => {
  const {
    documentationText: origDocText,
    type,
    label,
    render,
    quickFiltering,
  } = props;

  const documentationText = `${origDocText} Use "${quickFiltering}" for quick filtering.`;

  return {
    render,
    type,
    label,
    documentationText,
    getSuggestions: async (text: string) => {
      if (!text) {
        return Promise.resolve(yesNoOptions);
      }
      return Promise.resolve(
        yesNoOptions.filter(
          (s) =>
            s.text.toLowerCase().startsWith(text.toLowerCase()) ||
            s.value.toLowerCase().startsWith(text.toLowerCase())
        )
      );
    },
    shouldSuggest: (text: string) =>
      text.toLowerCase().startsWith(quickFiltering.toLowerCase()),
    toFilter: (value: string) => ({
      label: `${label}: ${yesNoOptions.find((s) => s.value === value)?.text}`,
      type,
      value,
    }),
    filterFromParams: async (params: URLSearchParams) => {
      const paramState = params.get(type) || null;

      return Promise.resolve(
        paramState
          ? {
              label: `${label}: ${paramState}`,
              value: paramState,
              type,
            }
          : null
      );
    },
    toParam: (filter: ActiveFilter) => filter.value ?? "",
  };
};

export function getYesNoFilterValue(
  filter: ActiveFilter | null | undefined
): boolean | null {
  if (filter?.value === "Yes") {
    return true;
  }

  if (filter?.value === "No") {
    return false;
  }

  return null;
}

export function useChargeBillableProvider(
  props: FilterProviderProps
): FilterProvider {
  const { documentationText } = props;
  return createYesNoProvider({
    documentationText,
    type: "billable",
    label: "Billable",
    quickFiltering: "Bill",
    render: () => (
      <YesNoPicker formField="billable" label="Billable" helperText="" />
    ),
  });
}

export function useShowFutureTimesheetsProvider(
  props: FilterProviderProps
): FilterProvider {
  const { documentationText } = props;
  return createYesNoProvider({
    documentationText,
    type: "showFuture",
    label: "Show Future",
    quickFiltering: "fut",
    render: () => (
      <YesNoPicker formField="showFuture" label="Show Future" helperText="" />
    ),
  });
}

export function useCertifiedPayrollProvider(
  props: FilterProviderProps
): FilterProvider {
  const { documentationText } = props;
  return createYesNoProvider({
    documentationText,
    type: "certifiedPayroll",
    label: "Certified Payroll",
    quickFiltering: "cert",
    render: () => (
      <YesNoPicker
        formField="certifiedPayroll"
        label="Certified Payroll"
        helperText=""
      />
    ),
  });
}

export function useShowArchivedProvider(
  props: FilterProviderProps
): FilterProvider {
  const { documentationText } = props;
  return createYesNoProvider({
    documentationText,
    type: "showArchived",
    label: "Show Archived",
    quickFiltering: "arc",
    render: () => (
      <YesNoPicker
        formField="showArchived"
        label="Show Archived"
        helperText=""
      />
    ),
  });
}
